/**
 * Rich Text Editor for newsletter articles (tiptap-based).
 * Supports: bold/italic/underline, headings, bullet list, external link, inline image, YouTube embed.
 */
import { useRef, useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline as UnderlineIcon, List, Link2, ImagePlus, Youtube, Heading2, Loader2,
} from 'lucide-react';
import { YouTubeEmbed } from './youtubeEmbed';

const TB_BTN =
  'p-1.5 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors';
const TB_BTN_ACTIVE = 'bg-[#8DC63F]/15 text-[#8DC63F]';

export default function RichTextEditor({ value, onChange, placeholder = 'Contenu de l\'article…', testId }) {
  const imageInputRef = useRef(null);
  const lastValueRef = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          style: 'color:#8DC63F;text-decoration:underline;font-weight:600;',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          style: 'max-width:100%;height:auto;display:block;margin:12px auto;border-radius:6px;',
        },
      }),
      YouTubeEmbed,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastValueRef.current = html;
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[140px] p-3 text-slate-700 leading-relaxed',
        'data-testid': testId || 'rich-text-editor',
      },
    },
  });

  // Sync external value changes (e.g. fetched from server) into editor
  useEffect(() => {
    if (!editor) return;
    if (value !== lastValueRef.current && !editor.isFocused) {
      lastValueRef.current = value;
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('URL du lien (https://…) :', previous || 'https://');
    if (url === null) return; // cancel
    if (url === '' || url === 'https://') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    let safe = url.trim();
    if (!/^https?:\/\//i.test(safe) && !safe.startsWith('mailto:')) safe = 'https://' + safe;
    editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run();
  }, [editor]);

  const insertYouTube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Lien YouTube (ex: https://youtube.com/watch?v=…) :', '');
    if (!url) return;
    const ok = editor.chain().focus().insertYouTube(url.trim()).run();
    if (!ok) toast.error('Lien YouTube invalide');
  }, [editor]);

  const handleImagePick = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !editor) return;
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop lourde (max 5 Mo)');
        return;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post('/newsletters/upload-inline-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        editor.chain().focus().setImage({ src: res.data.data_url, alt: '' }).run();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Erreur upload image');
      }
    },
    [editor]
  );

  if (!editor) {
    return (
      <div className="border border-slate-200 rounded-lg p-3 text-slate-400 text-sm flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement de l&apos;éditeur…
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg focus-within:border-[#8DC63F] focus-within:ring-1 focus-within:ring-[#8DC63F] transition-colors overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <button
          type="button" onClick={() => editor.chain().focus().toggleBold().run()}
          className={`${TB_BTN} ${editor.isActive('bold') ? TB_BTN_ACTIVE : ''}`}
          title="Gras (Ctrl+B)" data-testid="rt-bold"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`${TB_BTN} ${editor.isActive('italic') ? TB_BTN_ACTIVE : ''}`}
          title="Italique (Ctrl+I)" data-testid="rt-italic"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          type="button" onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`${TB_BTN} ${editor.isActive('underline') ? TB_BTN_ACTIVE : ''}`}
          title="Souligné (Ctrl+U)" data-testid="rt-underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button
          type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`${TB_BTN} ${editor.isActive('heading', { level: 2 }) ? TB_BTN_ACTIVE : ''}`}
          title="Sous-titre" data-testid="rt-h2"
        >
          <Heading2 className="h-4 w-4" />
        </button>
        <button
          type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`${TB_BTN} ${editor.isActive('bulletList') ? TB_BTN_ACTIVE : ''}`}
          title="Liste à puces" data-testid="rt-list"
        >
          <List className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button
          type="button" onClick={setLink}
          className={`${TB_BTN} ${editor.isActive('link') ? TB_BTN_ACTIVE : ''}`}
          title="Lien externe" data-testid="rt-link"
        >
          <Link2 className="h-4 w-4" />
        </button>
        <button
          type="button" onClick={() => imageInputRef.current?.click()}
          className={TB_BTN}
          title="Insérer une image" data-testid="rt-image"
        >
          <ImagePlus className="h-4 w-4" />
        </button>
        <button
          type="button" onClick={insertYouTube}
          className={TB_BTN}
          title="Insérer une vidéo YouTube" data-testid="rt-youtube"
        >
          <Youtube className="h-4 w-4 text-red-500" />
        </button>
        <input
          ref={imageInputRef} type="file" accept="image/*" onChange={handleImagePick}
          className="hidden"
        />
      </div>

      {/* Editor area */}
      <div className="bg-white">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
}
